import { ProductType, ResultType } from 'features/searchResultsFacets';
import HousingDevelopmentMarker from '../models/housingDevelopmentMarker';
import BuildablePlanMarker from '../models/buildablePlanMarker';
import PlanUnitMarker from '../models/planUnitMarker';
import SpecUnitMarker from '../models/specUnitMarker';
import MarkerType from '../enums/markerType';

/**
 * It filters out plans that are in the buildablePlans array from the plans array.
 * @param plans - An array of plans.
 * @param buildablePlans - An array of Housing Developments that has more than one plan.
 */
export const filterPlansFromBuildablePlans = (plans, buildablePlans) =>
  plans.filter((plan) => !buildablePlans.some((x) => x.GeoId === plan.GeoId));

/**
 * Get all housing developments that have more than one plan.
 * We are considering the totalPlans property exposed by the data source, and also the current
 * number of plans matching the sorting applied by the user. For example, if a community has 3 plans
 * but only 1 matches the current filters, we don't want to show it as a buildable plan.
 * @param housingDevelopments - An array of housing developments.
 */
export const getAllBuildablePlans = (housingDevelopments, plans) => {
  const featuredPlans = plans.filter(plan => plan.WasMatchedAsSpotlightResult).map(plan => plan.GeoId);

  const planCountMap = new Map();
  plans.forEach(plan => {
    planCountMap.set(plan.CommunityId, (planCountMap.get(plan.CommunityId) || 0) + 1);
  });

  const buildablePlans = housingDevelopments
    .filter(buildablePlan => buildablePlan.TotalPlans > 1)
    .filter(buildablePlan => {
      const count = planCountMap.get(buildablePlan.Id) || 0;
      return count > 1;
  });
  
  buildablePlans.filter(plan => featuredPlans.includes(plan.GeoId)).forEach(element => {
    element.WasMatchedAsSpotlightResult = true;
  });

  return buildablePlans;
};

/**
 * If the productType is 'communities', then create a new HousingDevelopmentMarker, otherwise create a
 * new HousingUnitMarker.
 * @param productType - 'communities' or 'homes'
 * @param allHousing - an array of objects
 * @returns An array of objects.
 */
export const getMarkers = (productType, allHousing) => {
  const isCommunitiesSearch = productType === ResultType.Communities.toString();
  const markers = [
    ...allHousing.specs.map((spec) => new SpecUnitMarker(spec)),
    ...allHousing.plans.map((plan) => new PlanUnitMarker(plan)),
    ...allHousing.buildablePlans.map((b) => new BuildablePlanMarker(b)),
  ];

  if (isCommunitiesSearch) {
    return allHousing.communities.map((housing) => new HousingDevelopmentMarker(housing));
  }

  return markers;
};

export const updatePageNumberOfMarkers = (productType, allHousing, markers) => {
  const isCommunitiesSearch = productType === ProductType.Communities.toString();
  const communityMarkers = markers.filter((marker) => marker.type === MarkerType.HousingDevelopment.toString());
  if (!isCommunitiesSearch ||
    !allHousing?.communities ||
    communityMarkers.length !== markers.length ||
    allHousing.communities.length !== markers.length) {
    return;
  }

  for (let i = 0; i < markers.length; i+=1) {
    const marker = markers[i];
    const sourceMarker = allHousing.communities[i];
    if(sourceMarker.Id === marker.id){
      marker.pageNumber = sourceMarker.Page;
    }
    else{
      const newPage = allHousing.communities.find((housing) => housing.Id === marker.id)?.Page;
      marker.pageNumber = newPage ?? marker.pageNumber;
    }      
  }
};

/**
 * Updates the given markers array with interactive site plan information from the provided sitePlans array.
 *
 * - Creates a map of site plans by communityId for efficient lookup.
 * - For each marker, checks if a matching site plan exists.
 * - If a matching site plan is found, sets marker.hasInteractiveSitePlan to true and updates
 *   marker.sitePlanCenterlongitude and marker.sitePlanCenterlatitude with the site plan's center coordinates.
 *
 * @param {Array} markers - The array of marker objects to update.
 * @param {Array} sitePlans - The array of site plan objects containing communityId and center coordinates.
 */
export const updateMarkersWithSitePlans = (markers, sitePlans) => {
  const sitePlanMap = new Map(sitePlans.map(sitePlan => [sitePlan.communityId, sitePlan]));

  markers.forEach(marker => {
    const sitePlan = sitePlanMap.get(marker.communityId);
    if (!sitePlan) { return; }
    
    marker.hasInteractiveSitePlan = true;
    marker.sitePlanCenterLongitude = sitePlan.topBottomLongitude;
    marker.sitePlanCenterLatitude = sitePlan.topBottomLatitude;
  });
};